// ─────────────────────────────────────────────────────
// Teaching roles (Rule 37 / TWO_BOARD_SPEC.md §2).
// Ms. Momo demonstrates on HER board (tutor board), the student acts on THEIR
// board. Every step maps to one role, derived from existing step fields only —
// lesson data is never changed to do this.
//
//   demo       — tutor plays the move, student copies it
//   demo-hint  — new topic: tutor demonstrates AND the student board hints (orange)
//   practice   — tutor demonstrates one example, no orange hints
//   test       — no demonstration, no hints (border hint only after 2 wrong)
//   explain    — tutor mirrors the student board
// ─────────────────────────────────────────────────────

const TEST_TYPES = [
  'piece-placement-unguided', 'multi-move-puzzle',
  'recap-quiz', 'true-or-false', 'name-the-square', 'file-name-quiz',
  'piece-spot-quiz', 'piece-letter-quiz', 'colour-quiz',
  'click-file', 'click-rank', 'click-square', 'independent-squares',
  'speed-intro', 'speed-round',
];

export function getTeachingRole(step) {
  const tt = step?.taskType;
  if (!tt) return 'explain';
  if (tt === 'observe') {
    return (step.demoSequence?.length || step.demoFen) ? 'demo' : 'explain';
  }
  if (tt === 'notation-demo') return 'demo';
  if (tt === 'piece-intro-guided' || tt === 'piece-placement' || tt === 'notation-drag') return 'demo-hint';
  if (tt === 'piece-range') return 'practice';
  if (TEST_TYPES.includes(tt)) return 'test';
  return 'explain';
}

// Test steps whose board highlight would reveal the answer (Rule 2 suppression).
// Quizzes like name-the-square / true-or-false / file-name-quiz keep their glow:
// there the highlighted square IS the question, not a hint.
export const HINTLESS_TEST_TYPES = [
  'click-file', 'click-rank', 'click-square', 'independent-squares', 'speed-round',
];

// Generic lines for copy tasks when a step has no successVoice / wrong voice of
// its own. Max 12 words each (Rule 28); rotated so no line repeats in a lesson
// until the pool is exhausted.
export const COPY_PRAISE = {
  young: [
    'Yay! You copied me perfectly!',
    'Woohoo! Just like me!',
    'Super star move! Well done!',
    'Brilliant! Your piece went exactly there!',
    'You did it! High five!',
    'Amazing copying! You are so clever!',
  ],
  mid: [
    'Nice work. Exactly right.',
    'Great copy. You have got it.',
    'Spot on. Same move as mine.',
    'Correct. Clean move.',
    'Well played. That is the move.',
    'Exactly right. Good eye.',
  ],
  teen: [
    'Correct. Clean execution.',
    'Precise. Next.',
    'Exactly. Well played.',
    'Right move. Good.',
    'Accurate. Moving on.',
    'Correct copy.',
  ],
};

export const COPY_RETRY = {
  young: [
    "Oopsie! Let's watch together again.",
    'Not quite! Watch my hand again.',
    "Almost! Let's watch one more time.",
  ],
  mid: [
    'Not quite. Watch my move once more.',
    'Close. Watch the move again.',
    'Not that one. Watch again closely.',
  ],
  teen: [
    'Incorrect. Watch the move again.',
    'Wrong square. Watch again.',
    'No. Review the move.',
  ],
};

// Returns the next unused line for this age group, cycling through the pool.
// `usedRef` is a ref object ({ current: {} }) owned by the lesson so rotation
// resets per lesson.
export function nextLine(pool, ageGroup, usedRef) {
  const lines = pool[ageGroup] || pool.mid;
  const key = (pool === COPY_PRAISE ? 'praise:' : 'retry:') + ageGroup;
  const used = usedRef.current[key] || 0;
  usedRef.current[key] = used + 1;
  return lines[used % lines.length];
}
